import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketComparisonChart, {
    MARKET_RANGE_PRESETS,
    colorForIndexKey,
} from '../Components/MarketComparisonChart';
import { filterQuotesByPriceRange } from '../Components/StockPriceChart';
import './Markets.css';

/** DB `markets.symbol` for index rows matches quotes service label: index key uppercased (e.g. tsx → TSX). */
function indexKeyToStoredSymbol(key) {
    return String(key).toUpperCase();
}

/** Title-case index key for display (e.g. russell2000 → Russell2000). */
function formatIndexTitle(key) {
    return String(key)
        .split('_')
        .map(part =>
            part.length ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : '',
        )
        .join(' ');
}

function formatYMD(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

const BULK_MODES = [
    { id: '1d', label: 'Day', hint: 'Last two calendar days (captures the latest session).' },
    { id: '1w', label: 'Week', hint: 'Seven calendar days ending today.' },
    { id: '1y', label: 'Year', hint: 'Roughly the past calendar year.' },
    { id: 'range', label: 'Range', hint: 'Choose start and end dates (inclusive).' },
];

const DEFAULT_COMPARE_KEYS = ['tsx', 'sp500'];

const Markets = () => {
    const navigate = useNavigate();
    const [indices, setIndices] = useState(null);
    const [loadMetaErr, setLoadMetaErr] = useState('');
    const [latestBySymbol, setLatestBySymbol] = useState({});
    const [loadingLatest, setLoadingLatest] = useState(false);
    const [latestErr, setLatestErr] = useState('');
    const [error, setError] = useState('');
    const [bulkMode, setBulkMode] = useState('1d');
    const [bulkRangeStart, setBulkRangeStart] = useState(() => {
        const start = new Date();
        start.setDate(start.getDate() - 30);
        return formatYMD(start);
    });
    const [bulkRangeEnd, setBulkRangeEnd] = useState(() => formatYMD(new Date()));
    const [bulkFetching, setBulkFetching] = useState(false);
    const [bulkStatus, setBulkStatus] = useState('');

    // Comparison chart state: checked index keys, per-symbol row cache, range.
    const [compareChecked, setCompareChecked] = useState(() => new Set(DEFAULT_COMPARE_KEYS));
    const [compareRows, setCompareRows] = useState({});
    const [compareLoading, setCompareLoading] = useState(0);
    const [compareErr, setCompareErr] = useState('');
    const [compareRange, setCompareRange] = useState('YTD');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadMetaErr('');
            try {
                const r = await fetch('/api/quotes/symbols', { credentials: 'include' });
                const data = await r.json();
                if (!r.ok) throw new Error(data.error || 'Failed to load index list');
                if (!cancelled) setIndices(data.indices || {});
            } catch (e) {
                if (!cancelled) setLoadMetaErr(e.message);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const loadLatestSummaries = useCallback(async (indicesObj) => {
        const keys = Object.keys(indicesObj || {});
        if (!keys.length) return;

        const symbols = keys.map(k => indexKeyToStoredSymbol(k));
        const param = symbols.map(s => encodeURIComponent(s)).join(',');
        setLoadingLatest(true);
        setLatestErr('');
        try {
            const r = await fetch(`/api/markets/latest?symbols=${param}`, { credentials: 'include' });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Failed to load latest prices');
            const list = Array.isArray(data) ? data : [];
            const map = {};
            for (const row of list) {
                if (!row.symbol) continue;
                map[row.symbol] = {
                    last_close: row.last_close ?? null,
                    last_quote_date: row.last_quote_date ?? null,
                };
            }
            setLatestBySymbol(map);
        } catch (e) {
            setLatestErr(e.message);
        } finally {
            setLoadingLatest(false);
        }
    }, []);

    useEffect(() => {
        if (!indices) return;
        loadLatestSummaries(indices);
    }, [indices, loadLatestSummaries]);

    const indexEntries = useMemo(() => {
        if (!indices) return [];
        return Object.entries(indices).sort(([a], [b]) =>
            a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
    }, [indices]);

    const loadCompareRows = useCallback(async (key) => {
        const sym = indexKeyToStoredSymbol(key);
        setCompareLoading(c => c + 1);
        setCompareErr('');
        try {
            const r = await fetch(`/api/markets?symbol=${encodeURIComponent(sym)}`, {
                credentials: 'include',
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || `Failed to load ${sym}`);
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => a.date.localeCompare(b.date));
            setCompareRows(prev => ({ ...prev, [key]: list }));
        } catch (e) {
            setCompareErr(e.message);
        } finally {
            setCompareLoading(c => c - 1);
        }
    }, []);

    // Fetch history for newly checked indices that aren't cached yet.
    useEffect(() => {
        for (const key of compareChecked) {
            if (!(key in compareRows)) {
                loadCompareRows(key);
            }
        }
    }, [compareChecked, compareRows, loadCompareRows]);

    const toggleCompare = (key) => {
        setCompareChecked(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const compareSeries = useMemo(() => {
        return indexEntries
            .filter(([key]) => compareChecked.has(key))
            .map(([key]) => ({
                key,
                label: indexKeyToStoredSymbol(key),
                color: colorForIndexKey(key),
                quotes: filterQuotesByPriceRange(compareRows[key] || [], compareRange),
            }))
            .filter(s => s.quotes.length > 0);
    }, [indexEntries, compareChecked, compareRows, compareRange]);

    const handleBulkUpdateAll = async () => {
        if (!indices) return;
        setBulkFetching(true);
        setError('');
        setBulkStatus('');
        try {
            const body =
                bulkMode === 'range'
                    ? { mode: 'range', start: bulkRangeStart, end: bulkRangeEnd }
                    : { mode: bulkMode };
            const r = await fetch('/api/markets/fetch/indices/all', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Bulk fetch failed');
            const errs = Array.isArray(data.errors) ? data.errors : [];
            const ok = data.indices_ok ?? 0;
            const total = data.indices_total ?? 0;
            const rows = data.rows_upserted ?? 0;
            let msg = `Updated ${ok}/${total} indices (${rows} row upserts) for ${data.start}–${data.end}.`;
            if (errs.length) {
                msg += ` ${errs.length} failed: ${errs.map(e => e.name).join(', ')}.`;
            }
            setBulkStatus(msg);
            await loadLatestSummaries(indices);
            // Comparison cache is stale after a bulk upsert — refetch checked series.
            setCompareRows({});
        } catch (e) {
            setError(e.message);
        } finally {
            setBulkFetching(false);
        }
    };

    const formatBarDateLocal = (ymd) => {
        if (!ymd) return '';
        const [y, m, d] = String(ymd).split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString(undefined, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatIndexClose = (price) =>
        new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        }).format(price);

    return (
        <div className="markets-page">
            <header className="markets-header">
                <h1>Market indices</h1>
                <p className="markets-lede">
                    Daily OHLCV for major indices is shared for all signed-in users. Data is loaded from Yahoo Finance
                    via this site&apos;s quotes service and stored in the database. Click an index to see its full
                    history and chart, or compare indices side by side at the bottom of the page.
                </p>
            </header>

            {indices && (
                <section className="markets-bulk-panel" aria-labelledby="markets-bulk-heading">
                    <h2 id="markets-bulk-heading" className="markets-bulk-title">
                        Update all indices
                    </h2>
                    <p className="markets-bulk-lede">
                        Fetches OHLCV from Yahoo for every configured index and upserts into the database for the
                        selected period.
                    </p>
                    <div className="markets-bulk-modes" role="group" aria-label="Date range for bulk update">
                        {BULK_MODES.map(({ id, label, hint }) => (
                            <button
                                key={id}
                                type="button"
                                className={`markets-bulk-mode-btn${bulkMode === id ? ' markets-bulk-mode-btn--active' : ''}`}
                                onClick={() => setBulkMode(id)}
                                title={hint}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {bulkMode === 'range' && (
                        <div className="markets-bulk-range">
                            <label className="markets-bulk-field">
                                <span>Start</span>
                                <input
                                    type="date"
                                    value={bulkRangeStart}
                                    onChange={e => setBulkRangeStart(e.target.value)}
                                />
                            </label>
                            <label className="markets-bulk-field">
                                <span>End</span>
                                <input
                                    type="date"
                                    value={bulkRangeEnd}
                                    onChange={e => setBulkRangeEnd(e.target.value)}
                                />
                            </label>
                        </div>
                    )}
                    <div className="markets-bulk-actions">
                        <button
                            type="button"
                            className="markets-fetch-btn"
                            onClick={handleBulkUpdateAll}
                            disabled={bulkFetching}
                        >
                            {bulkFetching ? 'Updating all indices…' : 'Fetch from Yahoo'}
                        </button>
                    </div>
                    {bulkStatus && <p className="markets-bulk-status">{bulkStatus}</p>}
                </section>
            )}

            {loadMetaErr && <div className="alert-error">{loadMetaErr}</div>}
            {latestErr && <div className="alert-error">{latestErr}</div>}
            {error && <div className="alert-error">{error}</div>}

            {!indices && !loadMetaErr && (
                <p className="markets-muted">Loading index list…</p>
            )}

            {indices && (
                <>
                    <div className="markets-grid">
                        {indexEntries.map(([key, ySym]) => {
                            const storedSymbol = indexKeyToStoredSymbol(key);
                            const snap = latestBySymbol[storedSymbol];
                            const hasQuote =
                                snap &&
                                snap.last_close != null &&
                                snap.last_quote_date;

                            return (
                                <div
                                    key={key}
                                    className="markets-index-card"
                                    onClick={() => navigate(`/markets/${key}`)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/markets/${key}`);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={
                                        hasQuote
                                            ? `View ${storedSymbol}, last close ${formatIndexClose(snap.last_close)} on ${snap.last_quote_date}`
                                            : `View ${storedSymbol} details`
                                    }
                                >
                                    <div className="markets-index-card-main">
                                        <span className="markets-index-symbol">{storedSymbol}</span>
                                        <span className="markets-index-yahoo">{ySym}</span>
                                    </div>
                                    <div className="markets-index-meta">
                                        <div className="markets-index-name">{formatIndexTitle(key)}</div>
                                        {loadingLatest && !hasQuote ? (
                                            <div className="markets-index-latest markets-index-latest--muted">
                                                Loading last close…
                                            </div>
                                        ) : hasQuote ? (
                                            <div className="markets-index-latest">
                                                <div className="markets-index-latest-price">
                                                    Last close {formatIndexClose(snap.last_close)}
                                                </div>
                                                <div className="markets-index-latest-line">
                                                    <span className="markets-index-latest-label">Trading day</span>
                                                    {' '}
                                                    {formatBarDateLocal(snap.last_quote_date)}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="markets-index-latest markets-index-latest--empty">
                                                No stored prices yet — open to load history.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <section className="markets-compare" aria-labelledby="markets-compare-heading">
                        <h2 id="markets-compare-heading" className="markets-compare-title">
                            Compare indices
                        </h2>
                        <p className="markets-bulk-lede">
                            Each index is rebased to its percent change from the start of the selected range, so
                            indices of different sizes share one scale.
                        </p>

                        <div className="markets-compare-checks" role="group" aria-label="Indices to compare">
                            {indexEntries.map(([key]) => (
                                <label key={key} className="markets-compare-check">
                                    <input
                                        type="checkbox"
                                        checked={compareChecked.has(key)}
                                        onChange={() => toggleCompare(key)}
                                    />
                                    <span
                                        className="markets-compare-swatch"
                                        style={{ backgroundColor: colorForIndexKey(key) }}
                                        aria-hidden="true"
                                    />
                                    <span className="markets-compare-check-label">
                                        {indexKeyToStoredSymbol(key)}
                                    </span>
                                </label>
                            ))}
                        </div>

                        <div
                            className="stock-chart-range-row"
                            role="group"
                            aria-label="Comparison chart range"
                        >
                            {MARKET_RANGE_PRESETS.map(p => (
                                <button
                                    key={p.id}
                                    type="button"
                                    className={`stock-chart-range-btn${compareRange === p.id ? ' is-active' : ''}`}
                                    onClick={() => setCompareRange(p.id)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        {compareErr && <div className="alert-error">{compareErr}</div>}

                        {compareChecked.size === 0 ? (
                            <p className="markets-muted">Check at least one index above to draw the chart.</p>
                        ) : compareLoading > 0 && compareSeries.length === 0 ? (
                            <p className="markets-muted">Loading index history…</p>
                        ) : compareSeries.length === 0 ? (
                            <p className="markets-muted">
                                No stored rows in this range for the selected indices. Use &quot;Update all
                                indices&quot; above or open an index and refresh its history.
                            </p>
                        ) : (
                            <MarketComparisonChart series={compareSeries} />
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default Markets;
